import {
  parseCsv,
  PO_COPY_BATCH_LIMIT,
  PO_COPY_MANIFEST_LIMIT,
  PO_COPY_EXPORT_BYTES,
  type PoCopyManifest,
  type PoCopyPage,
} from '@servwell/shared';
export interface PoCopyExportState {
  manifest: PoCopyManifest;
  remainingDocumentIds: string[];
  downloadedDocumentIds: string[];
  pageCount: number;
}
const bad = () =>
  new Error(
    'The saved-copy list or returned files could not be verified. No new file was downloaded. Start a new export.',
  );
const same = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);
async function sha(bytes: Uint8Array<ArrayBuffer>) {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('');
}
const encoded = (value: string) => new TextEncoder().encode(value);
export async function startPoCopyExport(
  manifest: PoCopyManifest,
): Promise<PoCopyExportState> {
  if (
    !manifest ||
    !Array.isArray(manifest.documents) ||
    !Number.isSafeInteger(manifest.count) ||
    manifest.count !== manifest.documents.length ||
    manifest.count > PO_COPY_MANIFEST_LIMIT ||
    !Number.isFinite(Date.parse(manifest.capturedAt))
  )
    throw bad();
  const documents = manifest.documents.map((d) => ({
    documentId: d.documentId,
    purchaseOrderId: d.purchaseOrderId,
    filename: d.filename,
    byteSize: d.byteSize,
    sha256: d.sha256,
    createdAt: d.createdAt,
  }));
  if (
    new Set(documents.map((d) => d.documentId)).size !== documents.length ||
    documents.some(
      (d) =>
        typeof d.documentId !== 'string' ||
        !d.documentId ||
        typeof d.purchaseOrderId !== 'string' ||
        !/^PO-[0-9]+[.]pdf$/.test(d.filename) ||
        !Number.isSafeInteger(d.byteSize) ||
        d.byteSize < 5 ||
        d.byteSize > 1048576 ||
        !/^[a-f0-9]{64}$/.test(d.sha256) ||
        !Number.isFinite(Date.parse(d.createdAt)),
    )
  )
    throw bad();
  if (
    encoded(JSON.stringify([{ result: { data: { json: manifest } } }])).byteLength >
      PO_COPY_EXPORT_BYTES ||
    (await sha(encoded(JSON.stringify(documents)))) !== manifest.cohortHash
  )
    throw bad();
  return {
    manifest: { ...manifest, documents },
    remainingDocumentIds: documents.map((d) => d.documentId),
    downloadedDocumentIds: [],
    pageCount: 0,
  };
}
export async function verifyPoCopyPage(
  state: PoCopyExportState,
  page: PoCopyPage,
): Promise<{ state: PoCopyExportState; file: PoCopyPage['file'] }> {
  const requested = state.remainingDocumentIds.slice(0, PO_COPY_BATCH_LIMIT);
  if (
    !page?.file ||
    typeof page.file.csv !== 'string' ||
    page.file.csv.length > PO_COPY_EXPORT_BYTES ||
    !Array.isArray(page.documentIds) ||
    !Array.isArray(page.remainingDocumentIds) ||
    !Number.isSafeInteger(page.file.rowCount) ||
    page.file.rowCount <= 0 ||
    page.file.rowCount !== page.documentIds.length ||
    page.file.rowCount > requested.length ||
    !same(page.documentIds, requested.slice(0, page.file.rowCount)) ||
    !same(page.remainingDocumentIds, requested.slice(page.file.rowCount))
  )
    throw bad();
  if (
    encoded(JSON.stringify([{ result: { data: { json: page } } }])).byteLength >
    PO_COPY_EXPORT_BYTES
  )
    throw bad();
  const [header, ...rows] = parseCsv(page.file.csv);
  if (
    !header ||
    !same(header, [
      'documentId',
      'purchaseOrderId',
      'filename',
      'byteSize',
      'sha256',
      'pdfBase64',
    ]) ||
    rows.length !== page.file.rowCount
  )
    throw bad();
  const expected = new Map(state.manifest.documents.map((d) => [d.documentId, d]));
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!,
      doc = expected.get(page.documentIds[i]!);
    if (
      row.length !== 6 ||
      !doc ||
      row[0] !== doc.documentId ||
      row[1] !== doc.purchaseOrderId ||
      row[2] !== doc.filename ||
      row[3] !== String(doc.byteSize) ||
      row[4] !== doc.sha256
    )
      throw bad();
    let binary: string;
    try {
      binary = atob(row[5]!);
    } catch {
      throw bad();
    }
    if (binary.length !== doc.byteSize || !binary.startsWith('%PDF-')) throw bad();
    const bytes = new Uint8Array(binary.length);
    for (let k = 0; k < binary.length; k++) bytes[k] = binary.charCodeAt(k);
    if ((await sha(bytes)) !== doc.sha256) throw bad();
  }
  const n = page.documentIds.length,
    next = {
      ...state,
      remainingDocumentIds: state.remainingDocumentIds.slice(n),
      downloadedDocumentIds: [...state.downloadedDocumentIds, ...page.documentIds],
      pageCount: state.pageCount + 1,
    };
  return {
    state: next,
    file: {
      ...page.file,
      filename: `supplier_copies_${state.manifest.cohortHash.slice(0, 12)}_part_${next.pageCount}.csv`,
    },
  };
}
function active(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException('Saved-copy export paused.', 'AbortError');
}
export async function downloadNextPoCopyPage(
  state: PoCopyExportState,
  fetchPage: (ids: string[], signal?: AbortSignal) => Promise<PoCopyPage>,
  download: (file: PoCopyPage['file']) => void | Promise<void>,
  signal?: AbortSignal,
): Promise<PoCopyExportState> {
  if (!state.remainingDocumentIds.length) return state;
  active(signal);
  const page = await fetchPage(
    state.remainingDocumentIds.slice(0, PO_COPY_BATCH_LIMIT),
    signal,
  );
  active(signal);
  const checked = await verifyPoCopyPage(state, page);
  active(signal);
  await download(checked.file);
  // Once the download callback runs, keep that progress even if a later cancellation arrives.
  return checked.state;
}
